import { createToggleState, type ToggleSnapshot } from './state/toggles';
import { renderChrome, renderFooter } from './ui/chrome';
import { renderInput } from './ui/input';
import { renderPanel } from './ui/panel';
import { renderDisclosure, renderRuleOfThumb } from './ui/footnote';
import { attachHover } from './ui/hover';
import { tokenize, tokenizeAll } from './tokenize/client';
import { renderMultipanel } from './ui/multipanel';
import { renderCalculator, type CalculatorBlock } from './ui/calculator';
import { premiumPct } from './data/premium';
import { pricing, oerePerCall as oereFn, type EncodingId } from './data/pricing';
import type { TokenizationResult } from './tokenize/types';
import { el } from './ui/dom';

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    import('virtual:pwa-register').then(({ registerSW }) => registerSW({ immediate: true }));
  });
}

const app = document.getElementById('app')!;
const toggles = createToggleState();
let detach: (() => void) | null = null;
let calc: CalculatorBlock | null = null;
let renderGen = 0;

const header = document.createElement('div');
const inputArea = document.createElement('div');
const countsArea = document.createElement('div');
countsArea.className = 'ow-counts-inline';
const panelArea = document.createElement('div');
panelArea.id = 'ow-panels';
const rotArea = document.createElement('div');
const moreToggleArea = document.createElement('div');
moreToggleArea.className = 'ow-more-toggle';
const calcArea = document.createElement('div');
const disclosureArea = document.createElement('div');
const qrArea = document.createElement('div');
qrArea.className = 'ow-qr';
const footer = document.createElement('div');
app.append(header, inputArea, countsArea, panelArea, rotArea, moreToggleArea, calcArea, disclosureArea, qrArea, footer);

renderChrome(header, toggles);
renderFooter(footer);
const input = renderInput(inputArea);
renderDisclosure(disclosureArea);
qrArea.appendChild(el('img', {
  attrs: { src: '/qr.png', alt: 'QR-kode til tokenizer.onewordlab.no', width: '128', height: '128' }
}));

const moreBtn = el('button', {
  class: 'ow-more', text: 'vis mer ↓',
  attrs: { type: 'button' }
});
moreBtn.addEventListener('click', () => toggles.toggle('expanded'));
moreToggleArea.appendChild(moreBtn);

function broadcast(r: TokenizationResult): Record<EncodingId, TokenizationResult> {
  return { p50k_base: r, cl100k_base: r, o200k_base: r };
}

async function rerender(): Promise<void> {
  const gen = ++renderGen;
  const text = input.getText();
  const enText = input.getEnglishText();
  const snap = toggles.snapshot();

  applyExpandedClass(snap);

  if (text.length === 0) {
    countsArea.replaceChildren();
    panelArea.replaceChildren();
    panelArea.classList.remove('ow-multipanel');
    rotArea.replaceChildren();
    if (detach) { detach(); detach = null; }
    return;
  }

  const needAllNo = snap.compareModels || calc != null;
  const needEn = snap.compareLanguages && enText.length > 0;
  const needAllEn = needEn && snap.compareModels;

  const noPromise: Promise<Record<EncodingId, TokenizationResult>> = needAllNo
    ? tokenizeAll(text)
    : tokenize(text, 'o200k_base').then(broadcast);
  const enPromise: Promise<Record<EncodingId, TokenizationResult> | null> = !needEn
    ? Promise.resolve(null)
    : needAllEn
      ? tokenizeAll(enText)
      : tokenize(enText, 'o200k_base').then(broadcast);

  const [noResults, enResults] = await Promise.all([noPromise, enPromise]);
  if (gen !== renderGen) return;

  const tokensCount = noResults.o200k_base.totalTokens;
  countsArea.textContent = `${tokensCount} tokens · ${text.length} tegn`;

  const enTarget = input.enRenderTarget();
  enTarget.replaceChildren();
  if (enResults) {
    enResults.o200k_base.tokens.forEach((t, i) => {
      enTarget.appendChild(el('span', {
        class: `ow-en-tok ow-en-tok-${i % 6}`,
        text: t.text
      }));
    });
  }

  panelArea.replaceChildren();
  panelArea.classList.remove('ow-multipanel');
  if (detach) { detach(); detach = null; }

  if (snap.compareModels) {
    renderMultipanel(panelArea, noResults, enResults, {
      showIds: snap.showIds, showCosts: snap.showCosts
    });
  } else {
    const result = noResults.o200k_base;
    const enResult = enResults?.o200k_base ?? null;
    const premium = enResult ? premiumPct(result.totalTokens, enResult.totalTokens) : null;
    const oere = snap.showCosts ? oereFn(result.totalTokens, pricing.o200k_base.usdPerMillion) : null;
    const single = document.createElement('div');
    panelArea.appendChild(single);
    renderPanel(single, result, { showIds: snap.showIds, premium, oerePerCall: oere });
  }
  detach = attachHover(panelArea);

  renderRuleOfThumb(rotArea);

  if (calc) {
    calc.setTokens({
      p50k_base: noResults.p50k_base.totalTokens,
      cl100k_base: noResults.cl100k_base.totalTokens,
      o200k_base: noResults.o200k_base.totalTokens
    });
  }
}

function applyExpandedClass(snap: ToggleSnapshot): void {
  document.body.classList.toggle('ow-expanded', snap.expanded);
  moreBtn.textContent = snap.expanded ? 'vis mindre ↑' : 'vis mer ↓';
}

input.onChange(() => { rerender(); });
toggles.subscribe(snap => {
  input.showEnglish(snap.compareLanguages && snap.expanded);
  if (snap.expanded && snap.showCosts && !calc) {
    calc = renderCalculator(calcArea, { p50k_base: 50, cl100k_base: 25, o200k_base: 14 });
  } else if ((!snap.expanded || !snap.showCosts) && calc) {
    calcArea.replaceChildren();
    calcArea.classList.remove('ow-calc');
    calc = null;
  }
  rerender();
});
rerender();
